import { Flex, IconButton } from '@chakra-ui/react';
import { LINE_CREATED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { getLineOrientation, getLineType } from 'libs/third-party/Mixpanel/mixpanel.helper';
import clonedeep from 'lodash.clonedeep';
import { getInitialWidgetPosition } from 'modules/Editor/Editor.helpers';
import { WidgetDragDropWrapper } from 'modules/Editor/components/WidgetDragDrop';
import { addNewWidget } from 'modules/Editor/store';
import { useAppDispatch } from 'modules/Editor/store/hooks';
import React, { ReactElement } from 'react';
import { WidgetType } from 'types/widget.types';
import { LINE_TYPE_MAP } from 'widgets/LineWidget/LineRenders';
import { generateDefaultData, getEndPos, getMidPosList, getStartPos } from 'widgets/LineWidget/LineWidget.helpers';
import { LineWidgetTypes, StraightLineProps } from 'widgets/LineWidget/LineWidget.types';

import {
  DEFAULT_BUTTON_SIZE,
  DEFAULT_ICON_SIZE,
  DEFAULT_ICON_STROKE_WIDTH,
  LINE_WIDGET_MENU_ITEMS,
} from './LineWidgetMenu.config';

type LineIconProps = {
  type: string;
  width: number;
  height: number;
  lineOptions: StraightLineProps;
};
const LineIcon = ({ type, width, height, lineOptions }: LineIconProps) => {
  const Line = LINE_TYPE_MAP[type as keyof typeof LINE_TYPE_MAP];
  return (
    <div style={{ width, height }}>
      <Line {...lineOptions}></Line>
    </div>
  );
};

export const LineWidgetMenu = (): ReactElement => {
  const dispatch = useAppDispatch();

  /**
   * Add widget to canvas using widget data from config file
   * The new widget dimensions will be scaled up from the icon dimensions
   * specified in the config file
   *
   * @param index {number} array index in LINE_WIDGET_MENU_ITEMS
   * @returns void
   */
  const doAddWidget = (index: number): void => {
    if (!dispatch) return;
    const widgetData = createWidgetData(index);

    dispatch(addNewWidget(widgetData));

    const { startPos, endPos, startArrowStyle, endArrowStyle, type } = widgetData.widgetData;
    Mixpanel.track(LINE_CREATED.eventName, {
      from: LINE_CREATED.from.LeftPanel,
      orientation: getLineOrientation(startPos, endPos),
      line_start: startArrowStyle === 'none' ? LINE_CREATED.lineEdge.None : LINE_CREATED.lineEdge.BasicArrow,
      line_end: endArrowStyle === 'none' ? LINE_CREATED.lineEdge.None : LINE_CREATED.lineEdge.BasicArrow,
      type: getLineType(type),
    });
  };

  const createWidgetData = (index: number) => {
    const defaultData = LINE_WIDGET_MENU_ITEMS[index];
    const newData = {
      ...defaultData.options,
      startPos: getStartPos(defaultData.options.posList),
      midPosList: getMidPosList(defaultData.options.posList),
      endPos: getEndPos(defaultData.options.posList),
      isDecorative: defaultData.isDecorative,
    };

    const widgetData = generateDefaultData(defaultData.type, newData);
    const { top: initialTopPx, left: initialLeftPx } = getInitialWidgetPosition();
    widgetData.widgetData = { ...widgetData.widgetData, topPx: initialTopPx, leftPx: initialLeftPx };
    return widgetData;
  };

  /**
   * Return the menu icon data using the actual line data for rendering the icon
   *
   * @param options StraightLineProps
   * @returns StraightLineProps
   */
  const getScaledData = (options: StraightLineProps): StraightLineProps => {
    // Update line size using the scale
    const scale = Math.min(
      DEFAULT_ICON_SIZE / Math.max(...options.posList.map((pos) => pos.xPx)),
      DEFAULT_ICON_SIZE / Math.max(...options.posList.map((pos) => pos.yPx)),
    );
    const lineOptions = clonedeep(options);
    lineOptions.strokeWidth = DEFAULT_ICON_STROKE_WIDTH;
    lineOptions.isReadonly = true;
    lineOptions.posList = lineOptions.posList.map((position) => ({
      xPx: position.xPx * scale,
      yPx: position.yPx * scale,
    }));

    return lineOptions;
  };

  /**
   * Render list of line widget based on config file
   */
  return (
    <Flex gap='8px' direction='row' wrap={'wrap'}>
      {LINE_WIDGET_MENU_ITEMS.map(({ type, label, options, dragDropImage, dragDropPosList }, index) => {
        const Line = LINE_TYPE_MAP[type];
        const lineOptions = getScaledData({
          ...options,
          posList: dragDropPosList || options.posList,
        });

        if (Line === null || !lineOptions) return null;

        const widthPx = Math.max(...lineOptions.posList.map((pos) => pos.xPx));
        const heightPx = Math.max(...lineOptions.posList.map((pos) => pos.yPx));
        return (
          <WidgetDragDropWrapper
            type={WidgetType.Line}
            widgetData={createWidgetData(index)}
            key={label}
            dragImage={
              <Flex
                height={`${DEFAULT_BUTTON_SIZE}px`}
                width={`${DEFAULT_BUTTON_SIZE}px`}
                alignItems='center'
                justifyContent='center'
                overflow='hidden'
              >
                {dragDropImage ? (
                  dragDropImage
                ) : (
                  <LineIcon
                    type={LineWidgetTypes.straight}
                    width={widthPx}
                    height={heightPx}
                    lineOptions={lineOptions}
                  />
                )}
              </Flex>
            }
            dragImageOffset={{ x: DEFAULT_BUTTON_SIZE / 2, y: DEFAULT_BUTTON_SIZE / 2 }}
            trackingEventData={{
              event_name: LINE_CREATED.eventName,
              properties: {
                from: LINE_CREATED.from.LeftPanel,
                orientation: getLineOrientation(
                  createWidgetData(index).widgetData.startPos,
                  createWidgetData(index).widgetData.endPos,
                ),
                line_start:
                  createWidgetData(index).widgetData.startArrowStyle === 'none'
                    ? LINE_CREATED.lineEdge.None
                    : LINE_CREATED.lineEdge.BasicArrow,
                line_end:
                  createWidgetData(index).widgetData.endArrowStyle === 'none'
                    ? LINE_CREATED.lineEdge.None
                    : LINE_CREATED.lineEdge.BasicArrow,
                type: getLineType(type),
              },
            }}
          >
            <IconButton
              width={`${DEFAULT_BUTTON_SIZE}px`}
              height={`${DEFAULT_BUTTON_SIZE}px`}
              aria-label={`Add ${label}`}
              overflow={'hidden'}
              icon={<LineIcon type={type} width={widthPx} height={heightPx} lineOptions={lineOptions} />}
              onClick={() => doAddWidget(index)}
              variant={'icon-btn-side-menu'}
            />
          </WidgetDragDropWrapper>
        );
      })}
    </Flex>
  );
};
